import {
  DynamicDrawUsage,
  GLSL3,
  InstancedMesh,
  LOD,
  Matrix4,
  PlaneGeometry,
  ShaderMaterial,
  type Material,
} from 'three';

const cache = new WeakMap<Material, ShaderMaterial>();

function shadowMaterial(base: Material): ShaderMaterial {
  const present = cache.get(base);
  if (present) return present;
  const amount = base instanceof ShaderMaterial ? base.uniforms['uPainterAmount'] : undefined;
  const material = new ShaderMaterial({
    glslVersion: GLSL3,
    transparent: true,
    depthWrite: false,
    depthTest: true,
    polygonOffset: true,
    polygonOffsetFactor: -1,
    polygonOffsetUnits: -1,
    uniforms: { uPainterAmount: amount ?? { value: 0 } },
    vertexShader: `
      out vec2 vCorner;
      out float vDistance;
      void main() {
        vec4 world = modelMatrix * instanceMatrix * vec4(position, 1.0);
        vCorner = uv * 2.0 - 1.0;
        vDistance = distance(cameraPosition, world.xyz);
        gl_Position = projectionMatrix * viewMatrix * world;
      }
    `,
    fragmentShader: `
      precision highp float;
      uniform float uPainterAmount;
      in vec2 vCorner;
      in float vDistance;
      layout(location = 0) out vec4 outColor;
      layout(location = 1) out vec4 outGeom;
      void main() {
        float radius = dot(vCorner, vCorner);
        float falloff = pow(max(0.0, 1.0 - radius), 2.0);
        float fade = 1.0 - smoothstep(35.0, 70.0, vDistance);
        float opacity = 0.22 * falloff * fade * (1.0 - uPainterAmount);
        if (opacity < 0.002) discard;
        outColor = vec4(0.0, 0.0, 0.0, opacity);
        outGeom = vec4(0.0);
      }
    `,
  });
  cache.set(base, material);
  base.addEventListener('dispose', () => {
    material.dispose();
    cache.delete(base);
  });
  return material;
}

/* The normal/ID attachment has zero alpha, so blending leaves the scene's geometry buffer intact.
   Render-time selection also keeps contact shadows out of the calibrated painter view. */
class ShadowRoot extends LOD {
  constructor(
    private readonly shadow: InstancedMesh,
    private readonly material: ShaderMaterial
  ) {
    super();
    this.name = 'contact-shadows';
    this.add(shadow);
  }

  override update(): void {
    this.shadow.visible =
      this.shadow.count > 0 && Number(this.material.uniforms['uPainterAmount']?.value ?? 0) < 0.5;
  }
}

export function createContactShadows(capacity: number, base: Material) {
  const geometry = new PlaneGeometry(2, 2).rotateX(-Math.PI / 2).translate(0, 0.025, 0);
  const material = shadowMaterial(base);
  const mesh = new InstancedMesh(geometry, material, capacity);
  mesh.name = 'contact-shadow-ellipses';
  mesh.frustumCulled = false;
  mesh.renderOrder = 4;
  mesh.count = 0;
  mesh.instanceMatrix.setUsage(DynamicDrawUsage);
  const root = new ShadowRoot(mesh, material);
  const local = new Matrix4();
  return {
    root,
    mesh,
    begin(): void {
      mesh.count = 0;
    },
    add(baseMatrix: Matrix4, length = 0.34, width = 0.24): void {
      if (mesh.count >= capacity) return;
      local.makeScale(length, 1, width).premultiply(baseMatrix);
      mesh.setMatrixAt(mesh.count++, local);
    },
    end(): void {
      mesh.instanceMatrix.needsUpdate = true;
      root.update();
    },
    dispose(): void {
      geometry.dispose();
      mesh.dispose();
    },
  };
}
